import type { Control, FieldValues } from "react-hook-form";
import type { SelectFieldViewProps } from "@/components/molecules/form/SelectFieldView";
import { SelectFieldView } from "@/components/molecules/form/SelectFieldView";
import {
  type EditorFieldWrapperProperties,
  EditorFieldWrapper,
} from "@/features/sparxsEditor/components/atoms/EditorFieldWrapper";
import { useEditorFieldState } from "@/features/sparxsEditor/hooks/editor/useEditorFieldState";
import { EDITOR_EDITED_HIGHLIGHT_CLASS } from "@/features/sparxsEditor/components/atoms/editorConstants";

export type EditorSelectFieldViewProps<
  TFieldValues extends FieldValues = Record<string, unknown>,
> = Omit<SelectFieldViewProps<TFieldValues>, "control"> &
  Pick<EditorFieldWrapperProperties, "toHide"> & {
    control?: Control<TFieldValues>;
  };

export const EditorSelectFieldView = <
  TFieldValues extends FieldValues = Record<string, unknown>,
>({
  label,
  id,
  control: controlProp,
  defaultValue,
  style,
  name,
  option1,
  option2,
  errorMessage,
  toHide = false,
  ...rest
}: EditorSelectFieldViewProps<TFieldValues>) => {
  const { control, errorMessageToShow, isEdited, dispatchUpdateFormState } =
    useEditorFieldState<TFieldValues>(name, {
      control: controlProp,
      errorMessage,
    });

  return (
    <EditorFieldWrapper toHide={toHide} isEdited={isEdited}>
      <SelectFieldView
        control={control}
        defaultValue={defaultValue}
        errorMessage={errorMessageToShow}
        id={id}
        label={label}
        labelClassName={isEdited ? EDITOR_EDITED_HIGHLIGHT_CLASS : undefined}
        name={name}
        option1={option1}
        option2={option2}
        onValueChange={dispatchUpdateFormState}
        style={style}
        {...rest}
      />
    </EditorFieldWrapper>
  );
};
